import { USERNAME_REGEX } from '../tutors/consts.js';
import { reservedWords } from '../auth/consts.js';

/**
 * Handle suggestion, shared by the server (which assigns one at signup) and the
 * claim flow (which offers a better one once it knows the provider's name).
 * Decorator-free so client components can import it.
 *
 * The rules are the ones handle systems converge on:
 *  - a person's own name beats a machine string — it is what they would type;
 *  - the bare handle is offered first, and a suffix is a last resort, not a habit;
 *  - the email's routing details (`+tag`, the domain) are never part of a public
 *    handle — publishing them leaks how to reach someone off-platform;
 *  - accents fold rather than vanish, so "José" becomes "jose", not "jos".
 */

export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_MAX_LENGTH = 20;

/** Lowercase, accent-folded, `[a-z0-9_]` only — the shape `USERNAME_REGEX` accepts. */
export function slugifyUsername(raw: string): string {
  return raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, USERNAME_MAX_LENGTH)
    .replace(/_+$/g, '');
}

/**
 * What a handle field should hold while someone is still typing: the same
 * character rules, but trailing separators survive so `anna_` can become
 * `anna_k`. Length is capped here because a field that accepts 60 characters
 * and a rule that accepts 20 is a rejection waiting to happen.
 */
export function sanitizeUsernameInput(raw: string): string {
  return raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+/g, '')
    .slice(0, USERNAME_MAX_LENGTH);
}

/** A handle we are willing to hand someone: right shape, not reserved. */
export function isUsableUsername(candidate: string): boolean {
  return USERNAME_REGEX.test(candidate) && !reservedWords.includes(candidate);
}

/** The email's local part with plus-addressing and the domain dropped. */
function emailBase(email: string): string {
  const local = (email.split('@')[0] ?? '').split('+')[0] ?? '';
  return slugifyUsername(local);
}

/**
 * Handles to offer, best first. The caller checks availability and takes the
 * first free one — nothing here touches the database.
 *
 * For "Anna Kovaleva <anna.k@gmail.com>": anna_kovaleva, anna, annakovaleva,
 * anna_k, anna_k (email) — deduped, so five ideas from two facts.
 */
export function usernameCandidates({ name, email }: { name?: string | null; email?: string | null }): string[] {
  const candidates: string[] = [];
  const push = (value: string) => {
    if (isUsableUsername(value) && !candidates.includes(value)) candidates.push(value);
  };

  const parts = (name ?? '')
    .split(/\s+/)
    .map((part) => slugifyUsername(part))
    .filter(Boolean);
  const [first, ...rest] = parts;
  const last = rest.at(-1);

  if (first && last) push(slugifyUsername(`${first} ${last}`));
  if (first) push(first);
  if (first && last) push(slugifyUsername(`${first}${last}`));
  if (first && last) push(slugifyUsername(`${first} ${last.slice(0, 1)}`));

  const fromEmail = emailBase(email ?? '');
  if (fromEmail) push(fromEmail);

  return candidates;
}

/**
 * The ladder a caller walks when the good handles are taken: the best candidate
 * with 2, 3, 4… appended. Small numbers read as "the second Anna", where a long
 * random run reads as a database id.
 */
export function suffixedCandidates(base: string, count: number): string[] {
  const room = USERNAME_MAX_LENGTH - String(count + 1).length;
  const trimmed = base.slice(0, room).replace(/_+$/g, '');
  const out: string[] = [];
  for (let n = 2; n <= count + 1; n++) {
    const candidate = `${trimmed}${n}`;
    if (isUsableUsername(candidate)) out.push(candidate);
  }
  return out;
}
